'use client'

import { useTranslation } from '@/hooks/client/use-translation'
import SvgSlidersSimple from '@/icons/regular/SlidersSimple'

import { ActiveFilters } from './filters/active-filters'
import { SearchField } from './search-field'
import { SortControl } from './sort-control'
import { trackDirectory } from './track-directory'
import { ActionType, countActiveFilters, type DirectoryAction, type DirectoryState } from './use-directory-reducer'

interface ResultsToolbarProps {
  state: DirectoryState
  dispatch: React.Dispatch<DirectoryAction>
  resultCount: number
  totalCount: number
  modelCount: number
  totalModelCount: number
  facetLabels: Map<string, string>
}

// Search, the phone-only Filters button, the live result count, the sort
// control and the active-filter pills — everything that acts on the list,
// directly above it.
export const ResultsToolbar = ({
  state,
  dispatch,
  resultCount,
  totalCount,
  modelCount,
  totalModelCount,
  facetLabels,
}: ResultsToolbarProps) => {
  const { t } = useTranslation()
  const activeCount = countActiveFilters(state.filters)
  const isFiltered = activeCount > 0 || state.term.trim().length >= 3

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-3">
        <SearchField
          value={state.term}
          onChange={(term) => dispatch({ type: ActionType.SetTerm, term })}
          className="min-w-0 flex-1"
        />
        <button
          type="button"
          data-tracking="ignore"
          aria-haspopup="dialog"
          onClick={() => dispatch({ type: ActionType.ToggleModal })}
          className="border-navy/20 text-navy hover:border-navy inline-flex min-h-[48px] shrink-0 cursor-pointer items-center gap-2 rounded-xl border bg-white px-4 text-[15px] font-semibold transition-colors lg:hidden"
        >
          <SvgSlidersSimple className="h-4 w-4 fill-current" aria-hidden />
          {t('client.directory.filters', 'Filters')}
          {activeCount > 0 && (
            <span className="bg-navy min-w-[22px] rounded-full px-1.5 py-1 text-center text-[12px] leading-none font-bold text-white">
              {activeCount}
            </span>
          )}
        </button>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-3">
        <p role="status" aria-live="polite" className="text-navy/70 text-[15px]">
          {isFiltered
            ? t(
                'directory.results.filtered',
                '{{familyCount}} of {{totalCount}} product families match ({{modelCount}} models)',
                { familyCount: resultCount, totalCount, modelCount }
              )
            : t('directory.results.all', '{{familyCount}} product families, {{modelCount}} models', {
                familyCount: totalCount,
                modelCount: totalModelCount,
              })}
        </p>
        <SortControl
          value={state.sort}
          onChange={(sort) => {
            trackDirectory('directory-sort', { sort })
            dispatch({ type: ActionType.SetSort, sort })
          }}
        />
      </div>

      <ActiveFilters filters={state.filters} facetLabels={facetLabels} dispatch={dispatch} />
    </div>
  )
}
